import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoArrowBack, IoCart, IoHomeOutline, IoHeartOutline, IoGridOutline, IoPersonOutline } from "react-icons/io5";

const CategoryProductsScreen = () => {
	const { name } = useParams();
	const navigate = useNavigate();
	const categoryName = name ? decodeURIComponent(name) : "";

	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [cartCounts, setCartCounts] = useState({});
	const [cartMessage, setCartMessage] = useState("");

	useEffect(() => {
		const fetchProducts = async () => {
			try {
				setLoading(true);
				setError(null);
				const endpoint =
					categoryName === "Fashion & Beauty"
						? `${process.env.REACT_APP_API_URL}/api/products/fashion-beauty`
						: `${process.env.REACT_APP_API_URL}/api/products/category?category=${encodeURIComponent(categoryName)}`;
				const response = await fetch(
					endpoint
				);
				if (!response.ok) throw new Error("Failed to fetch products");
				const data = await response.json();
				setProducts(Array.isArray(data?.data) ? data.data : []);
			} catch (err) {
				setError("Error fetching products.");
				setProducts([]);
			} finally {
				setLoading(false);
			}
		};
		if (categoryName) {
			fetchProducts();
		}
	}, [categoryName]);

	useEffect(() => {
		const fetchCart = async () => {
			try {
				const token = localStorage.getItem("userToken");
				const headers = { "Content-Type": "application/json" };
				if (token) headers["Authorization"] = `Bearer ${token}`;
				const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart`, {
					credentials: "include",
					headers,
				});
				if (response.status === 200) {
					const data = await response.json();
					const counts = {};
					if (data && data.data && Array.isArray(data.data.items)) {
						data.data.items.forEach(item => {
							counts[item.productId] = item.quantity;
						});
					}
					setCartCounts(counts);
				} else {
					setCartCounts({});
				}
			} catch (err) {
				setCartCounts({});
			}
		};
		fetchCart();
	}, []);

	const handleAdd = async (id) => {
		try {
			const token = localStorage.getItem("userToken");
			const response = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/add`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					...(token ? { Authorization: `Bearer ${token}` } : {}),
				},
				body: JSON.stringify({ productId: id, quantity: 1 }),
				credentials: "include"
			});
			const data = await response.json();
			const messageText = (data && data.message ? data.message : "").toLowerCase();
			const shouldRedirectToLogin =
				response.status === 401 ||
				messageText.includes("login") ||
				messageText.includes("access cart") ||
				messageText.includes("unauthorized");
			if (data.success) {
				setCartMessage(data.message);
				setTimeout(() => setCartMessage(""), 2000);
				setCartCounts((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
			} else {
				setCartMessage(data.message || "Failed to add product to cart.");
				setTimeout(() => setCartMessage(""), 2000);
				if (shouldRedirectToLogin) {
					setTimeout(() => {
						navigate("/login", {
							state: { loginMessage: data.message || "Login to access cart" }
						});
					}, 300);
				}
			}
		} catch (err) {
			setCartMessage("Failed to add product to cart.");
			setTimeout(() => setCartMessage(""), 2000);
		}
	};

	const handleRemove = (id) => {
		setCartCounts((prev) => {
			const newCounts = { ...prev };
			if (newCounts[id] > 1) {
				newCounts[id] = newCounts[id] - 1;
			} else {
				delete newCounts[id];
			}
			return newCounts;
		});
	};

	return (
		<div className="min-h-screen bg-[#F1F3F2] text-gray-900">
			{cartMessage && (
				<div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-[#19C463] text-white px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut" style={{ minWidth: '220px', textAlign: 'center', letterSpacing: '0.5px' }}>
					{cartMessage}
				</div>
			)}

			<header className="sticky top-0 z-20 bg-white shadow-sm">
				<div className="flex items-center justify-between p-2">
					<button
						type="button"
						onClick={() => navigate(-1)}
						className="h-10 w-10 rounded-full flex items-center justify-center"
						aria-label="Go back"
					>
						<IoArrowBack className="h-5 w-5 text-gray-700" />
					</button>
					<h1 className="text-base font-semibold text-gray-900">
						{categoryName || "Category"}
					</h1>
					<button
						type="button"
						className="h-10 w-10 bg-white flex items-center justify-center"
						aria-label="Cart"
						onClick={() => navigate("/cart")}
						style={{
							borderRadius: "4px",
							boxShadow: "0 0 4px rgba(0, 0, 0, 0.25)"
						}}
					>
						<IoCart className="h-5 w-5 text-gray-700" />
					</button>
				</div>
			</header>

			<main className="bg-white pb-24">
				<div className="grid grid-cols-2 gap-x-[2px] gap-y-[6px]">
					{loading ? (
						<div className="col-span-2 text-center py-8 text-gray-500">Loading products...</div>
					) : error ? (
						<div className="col-span-2 text-center py-8 text-red-500">{error}</div>
					) : !Array.isArray(products) || products.length === 0 ? (
						<div className="col-span-2 text-center py-8 text-gray-500">No products found.</div>
					) : (
						(products || []).map((product) => (
							<div
								key={product._id}
								className="rounded-lg p-1 flex flex-col gap-1"
							>
								<div
									className="rounded-t-[4px] aspect-[10/9] w-full overflow-hidden cursor-pointer"
									onClick={() => navigate(`/product/${product._id}`)}
								>
									<img
										loading="lazy"
										src={product.productImages && product.productImages[0] ? product.productImages[0] : '/placeholder.png'}
										alt={product.productName}
										className="h-full w-full object-cover"
									/>
								</div>
								<div
									className="mt-3 text-left cursor-pointer"
									onClick={() => navigate(`/product/${product._id}`)}
								>
									<p className="text-sm font-medium text-gray-900 truncate">
										{product.productName} - {product.productDescription}
									</p>
									{product.productQuantity <= 5 && (
										<span className="mt-1 inline-block text-xs font-bold text-[#19C463] tracking-wider" style={{ textTransform: 'uppercase' }}>
											ONLY 1 LEFT
										</span>
									)}
									<div className="mt-2 flex items-center gap-2">
										<span className="text-sm font-semibold text-gray-900">
											₦ {Number(product.productSellingPrice || 0).toLocaleString("en-NG")}
										</span>
										<span className="text-xs text-gray-500">{product.productSold}</span>
									</div>
								</div>

								<div className="mt-3">
									{cartCounts[product._id] > 0 ? (
										<div className="flex items-center justify-between px-2 py-1">
											<button
												type="button"
												onClick={() => handleRemove(product._id)}
												className="h-8 w-8 bg-[#19C463] text-white"
												aria-label="Decrease quantity"
												style={{ borderRadius: "4px" }}
											>
												-
											</button>
											<span className="text-sm font-bold text-black">{cartCounts[product._id]}</span>
											<button
												type="button"
												onClick={() => handleAdd(product._id)}
												className="h-8 w-8 bg-[#19C463] text-white"
												aria-label="Increase quantity"
												style={{ borderRadius: "4px" }}
											>
												+
											</button>
										</div>
									) : (
										<button
											type="button"
											onClick={() => handleAdd(product._id)}
											className="w-full flex items-center justify-center gap-2 rounded bg-[#19C463] px-1 py-2.5 text-sm font-medium text-white transition"
											style={{ borderRadius: "4px" }}
										>
											Add to cart
											<IoCart className="h-4 w-4" />
										</button>
									)}
								</div>
							</div>
						))
					)}
				</div>
			</main>

			<nav className="fixed bottom-0 inset-x-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.08)]">
				<div className="flex items-center justify-around py-3">
					<button
						type="button"
						onClick={() => navigate("/home")}
						className="flex flex-col items-center gap-1"
						style={{ color: "#000000" }}
					>
						<IoHomeOutline className="h-6 w-6" />
						<span className="p-1 text-sm">Home</span>
					</button>
					<button
						type="button"
						className="flex flex-col items-center gap-1"
						style={{ color: "#000000" }}
					>
						<IoHeartOutline className="h-6 w-6" />
						<span className="p-1 text-sm">Wishlist</span>
					</button>
					<button
						type="button"
						onClick={() => navigate("/category")}
						className="flex flex-col items-center gap-1"
						style={{ color: "#19C463" }}
					>
						<IoGridOutline className="h-6 w-6" />
						<span className="p-1 text-sm">Category</span>
					</button>
					<button
						type="button"
						onClick={() => navigate("/profile")}
						className="flex flex-col items-center gap-1"
						style={{ color: "#000000" }}
					>
						<IoPersonOutline className="h-6 w-6" />
						<span className="p-1 text-sm">Profile</span>
					</button>
				</div>
			</nav>
		</div>
	);
};

export default CategoryProductsScreen;


